import type { SVGProps } from "react";
import type { Tab } from "../ui-contract";
import type { R8HeaderIconId } from "./presentation";

export interface R8IconProps extends Omit<SVGProps<SVGSVGElement>, "children"> {
  icon: R8HeaderIconId;
}

const common = {
  viewBox: "0 0 32 32",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.8,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  focusable: "false" as const,
  "aria-hidden": true,
};

export function R8Icon({ icon, ...props }: R8IconProps) {
  if (icon === "shield") {
    return (
      <svg {...common} {...props}>
        <path d="M16 3.5 26 7v7.2c0 6.5-4.2 11.5-10 14.3C10.2 25.7 6 20.7 6 14.2V7l10-3.5Z" />
        <path d="m11.7 16 2.7 2.7 6-6.2" />
      </svg>
    );
  }

  if (icon === "shield-circle") {
    return (
      <svg {...common} {...props}>
        <circle cx="16" cy="16" r="13" />
        <path d="m16 7.7 6 2.1v4.4c0 3.9-2.5 7-6 8.8-3.5-1.8-6-4.9-6-8.8V9.8l6-2.1Z" />
        <path d="m13.2 15.4 2 2 3.8-4" />
      </svg>
    );
  }

  if (icon === "floodlight") {
    return (
      <svg {...common} {...props}>
        <path d="M7 8.5h13.5l3 9H10l-3-9Z" />
        <path d="M14 17.5v5.8M9.5 27h9M21.7 6l2.4-2.4M25.5 10.5H29M24 15l3 2" />
        <path d="M10 17.5 7.8 23" />
      </svg>
    );
  }

  if (icon === "beacon") {
    return (
      <svg {...common} {...props}>
        <path d="M10 20v-7a6 6 0 0 1 12 0v7M7.5 20h17v4h-17zM16 4V1.8M7.2 7.2 5.5 5.5M24.8 7.2l1.7-1.7M6 13H2.8M29.2 13H26" />
        <path d="M13 18v-5a3 3 0 0 1 6 0v5" />
      </svg>
    );
  }

  return (
    <svg {...common} {...props}>
      <path d="M9 18.5h14l-2-8h-8l-4 8ZM7 18.5h18V23H7zM13 10.5V7h6v3.5M5 11l-2-1.5M27 11l2-1.5M4.5 16H2M30 16h-2.5" />
      <path d="M12 23v3M20 23v3" />
    </svg>
  );
}

export function R8NavigationIcon({
  tab,
  ...props
}: Omit<SVGProps<SVGSVGElement>, "children"> & { tab: Tab }) {
  if (tab === "HOME") {
    return (
      <svg {...common} {...props}>
        <rect x="5" y="5" width="8" height="8" rx="2" />
        <rect x="19" y="5" width="8" height="8" rx="2" />
        <rect x="5" y="19" width="8" height="8" rx="2" />
        <rect x="19" y="19" width="8" height="8" rx="2" />
      </svg>
    );
  }

  if (tab === "SECURITY") {
    return (
      <svg {...common} {...props}>
        <path d="M16 3.5 26 7v7.2c0 6.5-4.2 11.5-10 14.3C10.2 25.7 6 20.7 6 14.2V7l10-3.5Z" />
        <path d="m11.7 16 2.7 2.7 6-6.2" />
      </svg>
    );
  }

  if (tab === "AUTOMATION") {
    return (
      <svg {...common} {...props}>
        <path d="m18.4 3.5-9 14h6.8l-2.6 11 9-14h-6.8l2.6-11Z" />
      </svg>
    );
  }

  if (tab === "EVENTS") {
    return (
      <svg {...common} {...props}>
        <circle cx="16" cy="16" r="12.5" />
        <path d="M16 9v7l4.5 3M16 3.5V6M28.5 16H26M16 28.5V26M3.5 16H6" />
      </svg>
    );
  }

  return (
    <svg {...common} {...props}>
      <rect x="5" y="5" width="22" height="22" rx="4" />
      <path d="M12.3 5v22M19.7 5v22M5 12.3h22M5 19.7h22" />
    </svg>
  );
}

export function R8RefreshIcon(props: Omit<SVGProps<SVGSVGElement>, "children">) {
  return (
    <svg {...common} {...props}>
      <path d="M25.2 11.5A10.5 10.5 0 1 0 26 18" />
      <path d="M25.2 5.8v5.7h-5.7" />
    </svg>
  );
}

export function R8ChevronRightIcon(props: Omit<SVGProps<SVGSVGElement>, "children">) {
  return (
    <svg {...common} {...props}>
      <path d="m12.5 7.5 8.5 8.5-8.5 8.5" />
    </svg>
  );
}

export function R8CheckIcon(props: Omit<SVGProps<SVGSVGElement>, "children">) {
  return (
    <svg {...common} {...props}>
      <path d="m7.5 16.2 5.2 5.1L24.5 9.5" />
    </svg>
  );
}

export function R8FailureMark(props: Omit<SVGProps<SVGSVGElement>, "children">) {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="currentColor"
      focusable="false"
      aria-hidden="true"
      {...props}
    >
      <path d="M8 1.2 15 14H1L8 1.2Zm-.8 4.1.2 5h1.2l.2-5H7.2Zm.8 7.4a.85.85 0 1 0 0-1.7.85.85 0 0 0 0 1.7Z" />
    </svg>
  );
}
